import { call, all, takeLatest, put, select, takeEvery, delay } from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import { request } from '../../xhr';
import { blockUser, blockUserAndRedirect, unblockUser, blockUserSuccess, blockUserFail, unblockUserSuccess, unblockUserFail, searchFriendsWithOptions, fetchMoreFriends, initializeFriendList } from './actionCreators';
import ErrorIcon from '@wix/da-ds/pkg/Icons/24x24/Error';
import { addNotification } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications';
import { selectors as streamSelectors, withOffset } from '../../Stream';
import { normalize } from 'normalizr';
import { userListSchema } from '../normalizr/schemas/user';
import { friendsStreamId } from './constants';
const logger = ddt.createLogger('usersaga');
export function* doBlockUser(userIdOrUuid, chatroomId) {
    return yield call(request, {
        method: 'post',
        url: '/user/block',
        data: {
            userid: userIdOrUuid,
            chatroom_id: chatroomId
        }
    }, ()=>({
            success: true
        }));
}
function* blockUserSaga(action) {
    if (!action.payload) {
        logger.error('Tried to block invalid user', action);
        return;
    }
    const { user } = action.payload;
    const result = yield call(doBlockUser, user.userId);
    if (result && result.success) {
        yield put(blockUserSuccess(user.userId));
    } else {
        yield put(blockUserFail(user, result));
    }
}
function* blockUserAndRedirectSaga(action) {
    if (!action.payload) {
        logger.error('Tried to block invalid userId', action);
        return;
    }
    const { user, redirectLocation } = action.payload;
    const result = yield call(doBlockUser, user.userId);
    if (result && result.success) {
        yield put(blockUserSuccess(user.userId));
        window.location.href = redirectLocation;
    } else {
        yield put(blockUserFail(user, result));
    }
}
function* unblockUserSaga(action) {
    if (!action.payload) {
        logger.error('Tried to unblock invalid userId', action);
        return;
    }
    const userId = action.payload;
    const result = yield call(request, {
        method: 'post',
        url: '/user/unblock',
        data: {
            userid: userId
        }
    }, ()=>({
            success: true
        }));
    if (result && result.success) {
        yield put(unblockUserSuccess(userId));
    } else {
        yield put(unblockUserFail(userId, result));
    }
}
function* handleBlockUserSuccess() {
    yield put(addNotification(NotificationType.Generic, {
        messageLocaleKey: 'common.notification.blockUser.success'
    }));
}
function* handleBlockUserFail(action) {
    var _result_errorDetails_reason, _result_errorDetails, _result;
    const { user, result } = action.payload;
    var _result_errorDetails_reason_toLowerCase;
    // common.notification.blockUser.fail.default etc
    const key = (_result_errorDetails_reason_toLowerCase = (_result = result) === null || _result === void 0 ? void 0 : (_result_errorDetails = _result.errorDetails) === null || _result_errorDetails === void 0 ? void 0 : (_result_errorDetails_reason = _result_errorDetails.reason) === null || _result_errorDetails_reason === void 0 ? void 0 : _result_errorDetails_reason.toLowerCase()) !== null && _result_errorDetails_reason_toLowerCase !== void 0 ? _result_errorDetails_reason_toLowerCase : 'default';
    yield put(addNotification(NotificationType.Generic, {
        messageLocaleKey: `common.notification.blockUser.fail.${key}`,
        messageLocaleOptions: {
            username: user.username
        },
        isError: true,
        cardProps: {
            iconComponent: ErrorIcon,
            multiLine: true,
            limitTextWidth: false,
            duration: 5000
        }
    }));
}
function* handleSearchFriends({ payload: { query, callback, isChatContext = false, includeNonFriendExactMatch, userType } }) {
    // debounce for fast typing
    yield delay(300);
    const searchQuery = query.trim().toLowerCase();
    if (searchQuery.length < 3) {
        callback([]);
        return;
    }
    const params = {
        search: includeNonFriendExactMatch ? searchQuery : undefined,
        letter: searchQuery[0],
        page: 0,
        is_chat: isChatContext,
        user_type: userType
    };
    const result = yield call(request, {
        method: 'get',
        url: `/user/friendsforletter`,
        params
    }, ()=>{
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line global-require
            const { users } = require('@wix/da-sample-data');
            return {
                users
            };
        }
    });
    if (!result || !result.users || result.users.length === 0) {
        return;
    }
    const found = result.users// include only those usernames that actually start with query
    .filter((u)=>u.username.toLowerCase().startsWith(searchQuery))// unique
    .filter((u, idx, self)=>self.findIndex((u2)=>u.userId === u2.userId) === idx);
    callback(found);
}
function* handleFetchMoreFriends({ payload: { streamId } }) {
    const { offset, limit } = yield select((state)=>({
            offset: withOffset.selectors.getNextOffset(state, streamId),
            limit: streamSelectors.getItemsPerFetch(state, streamId)
        }));
    const params = {
        user_type: 'deviant',
        offset,
        limit
    };
    const result = yield call(request, {
        method: 'get',
        url: '/user/friends',
        params
    }, ()=>{
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line global-require
            const { users } = require('@wix/da-sample-data');
            return {
                results: users,
                hasMore: false
            };
        }
    });
    if (!result) {
        yield put(withOffset.actions.fetchFailure({
            streamId,
            errorMsg: 'fetch error'
        }));
        return;
    }
    var _result_results;
    const { entities, result: items } = normalize((_result_results = result.results) !== null && _result_results !== void 0 ? _result_results : [], userListSchema);
    yield put(withOffset.actions.fetchSuccess({
        streamId,
        items,
        entities,
        hasMore: result.hasMore || false
    }));
}
function* initFriendList() {
    const isAlreadyInit = yield select(streamSelectors.isInitialized, friendsStreamId);
    if (isAlreadyInit) {
        return;
    }
    yield put(withOffset.actions.initialize({
        streamId: friendsStreamId,
        itemsPerFetch: 50,
        hasMore: true,
        fetchNextCallback: fetchMoreFriends
    }));
}
export default function* saga() {
    yield all([
        takeLatest(blockUser, blockUserSaga),
        takeLatest(unblockUser, unblockUserSaga),
        takeLatest(blockUserAndRedirect, blockUserAndRedirectSaga),
        takeLatest(blockUserSuccess, handleBlockUserSuccess),
        takeLatest(blockUserFail, handleBlockUserFail),
        takeLatest(searchFriendsWithOptions, handleSearchFriends),
        takeEvery(fetchMoreFriends, handleFetchMoreFriends),
        takeEvery(initializeFriendList, initFriendList)
    ]);
}
